import { FC } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FileProcess } from '../../graphql/generated';
import { pluralize } from '../../utils/pluralize/pluralize';

type FileProcessErrorsProps = {
  fileProcess: FileProcess;
  viewMore: (nextLimit: number) => Promise<void>;
};

export const DEFAULT_ERROR_SIZE = 3;

export const FileProcessErrors: FC<FileProcessErrorsProps> = ({
  fileProcess,
  viewMore,
}) => {
  const { name, errors } = fileProcess;

  if (!errors || errors.errors.length === 0) {
    return <Text data-testid="file-process">{name}</Text>;
  }

  const currentCount = errors.errors.length; // Only what is currently shown
  const totalCount = errors.count; // All the errors including those not shown

  const isPaginated = DEFAULT_ERROR_SIZE < totalCount;
  const hasMore = currentCount < totalCount;
  const remaining = totalCount - currentCount;
  const countOfErrorsToFetchNext =
    DEFAULT_ERROR_SIZE > remaining ? remaining : DEFAULT_ERROR_SIZE;
  const nextLimit = currentCount + countOfErrorsToFetchNext;

  return (
    <Stack pb={2} data-testid="file-process">
      <Flex justify="space-between">
        <Text>{name}</Text>
        <Text color="gray.500" size="xs" data-testid="total-errors">
          {totalCount} {pluralize('error', totalCount)}
        </Text>
      </Flex>
      <Stack pl={4}>
        {errors.errors.map((error) => {
          const uniqueKey = `${error.code}-${error.message?.slice(0, 50) || 'no-message'}`;
          
          return (
            <Alert
              key={uniqueKey}
              status="error"
              variant="left-accent"
              data-testid="file-process-error"
            >
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          );
        })}

        {isPaginated && hasMore && (
          <Flex justify="space-between" align="baseline">
            <Text color="gray.500">
              Showing {currentCount} of {totalCount} errors
            </Text>
            <Button
              onClick={() => viewMore(nextLimit)}
              colorScheme="blue"
              variant="ghost"
              data-testid="view-more-button"
            >
              View {countOfErrorsToFetchNext} more
            </Button>
          </Flex>
        )}

        {isPaginated && !hasMore && (
          <Text color="gray.500">All {totalCount} errors shown</Text>
        )}
      </Stack>
    </Stack>
  );
};
